import { useContext, useEffect, useRef, useState } from 'react';
import { UploadFileButton } from '../UploadFileButton';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop } from 'Hooks';
import { FileRow } from '../FileRow';
import { Box, Button, Divider, Avatar, Typography, Stack } from '@mui/material';
import { RequestsContext } from 'Context';
import { IFormMetaData, IRequestsFieldData } from 'API';
import { SelectRequestType } from '../SelectRequestType';
import { checkExtension, checkFileSize } from '../Utils';
import { DeleteModal2 } from 'Components';
import { PlusBtn, PurpleClose, UploadDraggingFile } from 'Images';
import { IError } from '..';
import { MAX_FILE_SIZE_IN_MB } from 'Config';
import { isFileValid } from 'Utils/isFileValid';
import { getCleanFileName } from 'Utils';

interface IFileUploadAreaProps {
  uploadFilesList: IFormMetaData | undefined;
  fieldAttachmentsData: IRequestsFieldData;
  index: number;
  errors: IError;
  setErrors: (errors: IError) => void;
  subjectLabel: string;
}

export const FileUploadArea = ({
  uploadFilesList,
  fieldAttachmentsData,
  index,
  errors,
  setErrors,
  subjectLabel,
}: IFileUploadAreaProps) => {
  const { requestsData, setRequestsData } = useContext(RequestsContext);
  const [showError, setShowError] = useState(false);
  const [showFileError, setShowFileError] = useState(false);
  const [showDuplicateFileError, setDuplicateFileError] = useState<
    [boolean, string]
  >([false, '']);
  const [showUploadArea, setShowUploadArea] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [fileDraggingIndex, setFileDraggingIndex] = useState<number | null>(
    null
  );
  const dictionary = useDictionary(Dictionary, DICTIONARY.UniversalRequest);
  const fileInput = useRef<HTMLInputElement[]>([]);
  const [currFile, setCurrFile] = useState<{
    name: string;
    index: number;
  } | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const isDesktop = useIsDesktop();

  const checkTotalFileSize = (selectedFile?: File) => {
    let totalSize = 0;
    requestsData.fieldAttachmentsData.forEach((field) => {
      Object.values(field.attachments).forEach((file) => {
        totalSize += (file.size + (selectedFile?.size || 0)) / 1024 / 1024;
      });
    });

    return totalSize < MAX_FILE_SIZE_IN_MB;
  };

  const handleFiles = async (files: FileList, currentFieldIndex: number) => {
    if (!fieldAttachmentsData.fieldId) {
      setErrors({ ...errors, isTypeError: true });
      return;
    }
    // Check if file with the same name already exists
    const fileName = getCleanFileName(files[0].name);
    if (fieldAttachmentsData.attachments.hasOwnProperty(fileName)) {
      setDuplicateFileError([true, fileName]);
      return;
    } else {
      setDuplicateFileError([false, '']);
    }

    if (checkFileSize(files[0]) && checkExtension(files[0])) {
      if (!checkTotalFileSize(files[0])) {
        setErrorMsg(Dictionary[DICTIONARY.General].totalFileSizeError);
        return;
      } else setErrorMsg('');
      if (!(await isFileValid(files[0]))) {
        setShowError(true);
        setShowFileError(true);
        return;
      }
      const fileName = getCleanFileName(files[0].name);
      setRequestsData((prevRequestData) => {
        const clonePrevRequestData = { ...prevRequestData };
        clonePrevRequestData.fieldAttachmentsData[currentFieldIndex] = {
          fieldId:
            requestsData.fieldAttachmentsData[currentFieldIndex].fieldId || '',
          attachments: {
            ...prevRequestData.fieldAttachmentsData[currentFieldIndex]
              ?.attachments,
            [fileName]: files[0],
          },
        };
        return clonePrevRequestData;
      });
      setErrors({ ...errors, isFileError: false });
      setShowError(false);
      setShowUploadArea(false);
      return;
    } else setErrorMsg('');
    setShowFileError(false);
    setShowError(true);
  };

  const onChangeHandler = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    event.preventDefault();
    const file = event.target.files;
    if (file !== null) {
      handleFiles(file, index);
    }
  };

  const handleOnDrop = (
    event: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    event.preventDefault();
    const droppedFiles = event.dataTransfer.files;
    handleFiles(droppedFiles, index);
    setFileDraggingIndex(null);
  };

  const watchFile = async (fileData: File, fileName: string) => {
    if (fileData) window.open(URL.createObjectURL(fileData));
  };

  const clickFileInput = (index: number) => {
    if (fileInput.current) {
      fileInput.current[index].click();
    }
  };

  const handleDragEnter = (
    event: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    event.preventDefault();
    if (fileDraggingIndex !== index) setFileDraggingIndex(index);
  };

  const handleDragLeave = (
    event: React.DragEvent<HTMLDivElement>,
    index: number
  ) => {
    event.preventDefault();
    setFileDraggingIndex(null);
  };

  const handleRequestTypeChange = (index: number, value: string) => {
    setErrors({ ...errors, isTypeError: false });
    setRequestsData((prevRequestData) => {
      const clonePrevRequestData = { ...prevRequestData };
      clonePrevRequestData.fieldAttachmentsData[index].fieldId = value;
      return clonePrevRequestData;
    });
  };

  const deleteFile = async (index: number) => {
    setRequestsData((prevRequestData) => {
      const clonePrevRequestData = { ...prevRequestData };

      delete clonePrevRequestData.fieldAttachmentsData[index].attachments[
        currFile?.name || ''
      ];

      return clonePrevRequestData;
    });

    setCurrFile(null);
    setShowDeleteModal(false);
  };

  useEffect(() => {
    if (Object.keys(fieldAttachmentsData?.attachments).length === 0) {
      setShowUploadArea(true);
    }
  }, [Object.keys(fieldAttachmentsData?.attachments).length]);

  const handleDeleteField = (fieldIndex: number) => {
    setRequestsData((prevRequestData) => {
      const clonePrevRequestData = { ...prevRequestData };

      clonePrevRequestData.fieldAttachmentsData =
        clonePrevRequestData.fieldAttachmentsData.filter(
          (fieldAttachment, index) => index !== fieldIndex
        );

      return clonePrevRequestData;
    });
  };

  useEffect(() => {
    if (
      (!fieldAttachmentsData.fieldId &&
        Object.keys(fieldAttachmentsData.attachments).length > 0) ||
      errors.isTypeError
    ) {
      setErrorMsg(dictionary.uploadFile.errorMsg);
    } else {
      setErrorMsg('');
    }
  }, [
    fieldAttachmentsData.attachments,
    fieldAttachmentsData.fieldId,
    errors.isTypeError,
  ]);

  const handleValidate = () => {
    if (
      Object.keys(fieldAttachmentsData?.attachments).length === 0 ||
      fieldAttachmentsData.fieldId === ''
    ) {
      setErrors({ ...errors, isTypeError: true });
      return false;
    }
    return true;
  };

  useEffect(() => {
    setErrors({ isFileError: false, isTypeError: false });
  }, []);

  return (
    <>
      <Box
        sx={{
          background: isDesktop ? '#fff' : '#E7EDFF',
          padding: isDesktop ? '16px 24px' : '16px',
          borderRadius: '16px',
          mb: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
        className={'uploadBox'}
        component='fieldset'
      >
        <legend>{dictionary.uploadFile.chooseFiles}</legend>
        <Button
          startIcon={
            <Avatar
              alt={''}
              sx={
                isDesktop
                  ? { width: '20px', height: '20px' }
                  : { width: '16px', height: '16px' }
              }
              src={PurpleClose}
            />
          }
          onClick={() => handleDeleteField(index)}
          sx={{
            height: '24px',
            color: '#1552E8',
            fontWeight: '600',
            fontSize: isDesktop ? '20px' : '16px',
            display: 'flex',
            width: '72px',
            alignSelf: 'end',
            mb: 1,
          }}
        >
          {dictionary.uploadFile.remove}
        </Button>
        <SelectRequestType
          value={fieldAttachmentsData.fieldId}
          onChange={(value) => handleRequestTypeChange(index, value)}
          uploadFilesList={uploadFilesList}
          errorMsg={errorMsg}
        />
        {requestsData.fieldAttachmentsData &&
        Object.keys(fieldAttachmentsData?.attachments || {}).length > 0 ? (
          <>
            {fieldAttachmentsData?.attachments &&
              Object.entries(fieldAttachmentsData.attachments).map((field) => {
                return (
                  <FileRow
                    key={field[0]}
                    fileName={field[0]}
                    watchFile={() => watchFile(field[1], field[0])}
                    deleteFile={() => {
                      setCurrFile({ name: field[0], index });
                      setShowDeleteModal(true);
                    }}
                  />
                );
              })}
            {showDuplicateFileError[0] && (
              <Typography
                color='error'
                fontSize='14px'
                textAlign='left'
                maxWidth='310px'
                m={isDesktop ? '-20px 0 30px' : '16px 0 20px'}
                top={'-20px'}
                position={'relative'}
                role={Boolean(showDuplicateFileError) ? 'alert' : undefined}
              >
                {dictionary.uploadFile.duplicateFileError(
                  showDuplicateFileError[1]
                )}
              </Typography>
            )}
          </>
        ) : isDesktop ? (
          <Typography variant='body2'>
            {dictionary.uploadFile.addFile}
          </Typography>
        ) : (
          <></>
        )}

        {Object.keys(fieldAttachmentsData?.attachments || {}).length > 0 && (
          <Button
            sx={{
              position: 'relative',
              color: 'primary.main',
              fontSize: isDesktop ? '18px' : '16px',
              width: isDesktop ? '170px' : '125px',
              mt: '-40px',
              height: '32px',
              padding: isDesktop ? undefined : '0',
              '&:focus': { outline: isDesktop ? undefined : 'none' },
            }}
            startIcon={
              <Avatar
                alt=''
                src={PlusBtn}
                sx={{ width: '13px', height: '13px' }}
              />
            }
            onClick={() => {
              setShowUploadArea(true);
            }}
            aria-label={dictionary.successDialog.uploadMoreFileOfType(
              subjectLabel
            )}
          >
            {dictionary.successDialog.uploadMoreFile}
          </Button>
        )}

        {showError ? (
          <Typography
            color='error.main'
            variant='body6'
            textAlign='left'
            maxWidth='310px'
            m={isDesktop ? '0 0 20px' : '16px 0 20px'}
            role={Boolean(showError) ? 'alert' : undefined}
          >
            {showFileError
              ? Dictionary[DICTIONARY.General].invalidFileError
              : dictionary.uploadFile.fileInfo}
          </Typography>
        ) : null}

        {showUploadArea && (
          <Box
            onClick={() => !fieldAttachmentsData.fieldId && handleValidate()}
          >
            <Box
              sx={
                !fieldAttachmentsData.fieldId
                  ? { zIndex: 999, pointerEvents: 'none', opacity: 0.5 }
                  : {}
              }
            >
              {isDesktop ? (
                <Box
                  sx={{
                    border:
                      fileDraggingIndex === index
                        ? '2px dashed #A020FF'
                        : '1px solid #B7CCFF',
                    borderColor: errors.isFileError ? 'error.main' : undefined,
                    padding: '24px',
                    background:
                      fileDraggingIndex === index ? '#FBFCFF' : '#E7EDFF',
                    borderRadius: '8px',
                    textAlign: 'center',
                    mt: 2,
                    position: 'relative',
                  }}
                  aria-invalid={errors.isFileError}
                  tabIndex={!fieldAttachmentsData.fieldId ? -1 : undefined}
                >
                  <Box
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={(e) => handleOnDrop(e, index)}
                    onDragEnter={(e) => handleDragEnter(e, index)}
                    onDragLeave={(e) => handleDragLeave(e, index)}
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      bgcolor: 'transparent',
                      top: 0,
                      left: 0,
                      zIndex: fileDraggingIndex === index ? 1 : undefined,
                    }}
                  />
                  {fileDraggingIndex === index ? (
                    <Stack padding='18px' mt='-1px'>
                      <Avatar
                        src={UploadDraggingFile}
                        alt=''
                        sx={{
                          width: '48px',
                          height: '48px',
                          margin: '12px auto',
                        }}
                      />
                      <Typography
                        sx={{
                          pointerEvents: 'none',
                          color: '#344266',
                        }}
                        variant='body6'
                      >
                        {dictionary.uploadFile.dragFilesHere}
                      </Typography>
                    </Stack>
                  ) : (
                    <Box>
                      <Typography
                        sx={{
                          pointerEvents: 'none',
                          color: '#344266',
                        }}
                        variant='body3'
                      >
                        {dictionary.uploadFile.dragFilesHere}
                      </Typography>
                      <Divider
                        aria-hidden='true'
                        sx={{ margin: '20px 0', pointerEvents: 'none' }}
                      >
                        {dictionary.uploadFile.or}
                      </Divider>
                      <input
                        ref={(e) => {
                          fileInput.current[index] = e!;
                        }}
                        type='file'
                        accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
                        hidden
                        onChange={(e) => onChangeHandler(e, index)}
                        id={`upload-file-${index}`}
                      />
                      <label
                        htmlFor={`upload-file-${index}`}
                        className='sr-only'
                      >
                        {`${dictionary.uploadFile.chooseFiles}`}
                      </label>
                      <UploadFileButton
                        isDataRequired={false}
                        clickHandler={() => clickFileInput(index)}
                        ref={null}
                        dataId={fieldAttachmentsData.fieldId}
                        isFileError={errors.isFileError}
                        ariaDescribedBy={
                          errors.isFileError
                            ? `upload-file-error-${index}`
                            : 'upload-file-info'
                        }
                      >
                        {dictionary.uploadFile.chooseFiles}
                      </UploadFileButton>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box>
                  <input
                    ref={(e) => {
                      fileInput.current[index] = e!;
                    }}
                    type='file'
                    accept='.doc,.docx,.jpg,.png,.pdf,.tif,tiff,jpeg'
                    hidden
                    onChange={(e) => onChangeHandler(e, index)}
                    id={`upload-file-${index}`}
                  />
                  <label htmlFor={`upload-file-${index}`} className='sr-only'>
                    {`${dictionary.uploadFile.chooseFiles}`}
                  </label>

                  <UploadFileButton
                    isDataRequired={false}
                    clickHandler={() => clickFileInput(index)}
                    ref={null}
                    dataId={fieldAttachmentsData.fieldId}
                    isFileError={errors.isFileError}
                    ariaDescribedBy={
                      errors.isFileError
                        ? `upload-file-error-${index}`
                        : 'upload-file-info'
                    }
                  >
                    {dictionary.uploadFile.addFile}
                  </UploadFileButton>
                </Box>
              )}
              {errors.isFileError && (
                <Typography
                  fontSize='14px !important'
                  role='alert'
                  aria-live='assertive'
                  color='error.main'
                  id={`upload-file-error-${index}`}
                >
                  {dictionary.uploadFile.fileErrorMsg}
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </Box>

      {showDeleteModal && currFile && (
        <DeleteModal2
          isVisible={showDeleteModal}
          closeModal={() => setShowDeleteModal(false)}
          deleteFile={() => deleteFile(currFile.index)}
          fileName={currFile.name}
        />
      )}
    </>
  );
};
